import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  CurrencyFormatters,
  ensureExhaustive,
  getPriceString,
  PremiumCollectionLodgingPromotionSearch,
  PremiumCollectionPromotionAvailabilityCard,
  PromotionAvailability,
  useDeviceTypes,
  validateRestProps,
} from "halifax";
import React, { useEffect } from "react";
import { RouterProps, useHistory } from "react-router";
import { v4 as uuidv4 } from "uuid";
import {
  GuestsSelection,
  Initial,
  LodgingCollectionEnum,
  PlatformEnum,
  Prices,
  RewardsAccount,
  RewardsPrice,
  RewardsCurrency,
  ValidRewardsCurrency,
} from "redmond";
import { IOfferAvailabilityRequest } from "redmond/hotels-module/interfaces";
import { hotelStarRatingToNumber } from "redmond/hotels-module/utils";
import { localCache } from "@capone/common";
import { LUXURY_COMPLIMENTARY_NIGHT_PROMOTION } from "@capone/experiments";
import { PremierCollectionAvailabilityConnectorProps } from "./container";
import { REQUEST_STATUS } from "./reducer/requestState";
import styles from "./styles.module.scss";
import { PATH_SHOP, PATH_HOME } from "../../utils/paths";
import { transformToStringifiedQuery } from "../shop/utils/queryStringHelpers";
import {
  AVAILABLE,
  CACHE_HOTEL_TOKEN,
  getExperimentVariant,
  useExperiments,
} from "../../context/experiments";
import { useOffersSearchParams } from "./utils/useOffersSearchParams";

function formatPrice(price: Prices | undefined): string | undefined {
  if (!price) {
    return undefined;
  }

  return getPriceString({
    price: price.fiat.value,
    currencySymbol: price.fiat.currencySymbol,
  });
}

function formatRewards(rewards: RewardsPrice | undefined): string | undefined {
  if (!rewards) {
    return undefined;
  }

  return CurrencyFormatters.rewards(rewards.value, rewards.currency);
}

const PremiumCollectionOffersSuccess = ({
  params,
  data,
  rewardsAccount,
  setSelectedLodgingIndex,
  cacheHotelTokenEnabled,
}: {
  params: IOfferAvailabilityRequest;
  data: Initial;
  rewardsAccount: RewardsAccount | undefined;
  setSelectedLodgingIndex: (index: number) => void;
  cacheHotelTokenEnabled: boolean;
}) => {
  const history = useHistory();
  const { matchesMobile } = useDeviceTypes();
  return (
    <Box className={clsx(styles.lodgingsResult)}>
      {data.lodgings.map((lodging, index) => {
        // TODO: CLUX-627 - Add a proper error state or filtering accordingly, move it to the Saga
        if (
          !lodging.lodgingCollection ||
          lodging.lodgingCollection === LodgingCollectionEnum.NoCollection
        ) {
          return "No collection";
        }
        if (!lodging.nthNightPromotion) {
          return "No promotion";
        }
        if (!lodging.nthNightPromotion.promotionStartDate) {
          return "No promotion start date";
        }
        if (!lodging.nthNightPromotion.promotionEndDate) {
          return "No promotion end date";
        }

        // TODO: CLUX-627 - Move the logic and transformation to the Selectors
        const hasAllPrices =
          lodging.price &&
          lodging.price.nightlyDiscountAware?.priceWithAllDiscounts &&
          lodging.price.nightlyDiscountAware?.priceWithNoDiscounts &&
          (
            lodging.price.totalDiscountAwareTaxesAndFeesIncluded ||
            lodging.price.totalDiscountAware
          )?.priceWithAllDiscounts &&
          (
            lodging.price.totalDiscountAwareTaxesAndFeesIncluded ||
            lodging.price.totalDiscountAware
          )?.priceWithNoDiscounts;

        const offerAvailability = hasAllPrices
          ? PromotionAvailability.PromotionAvailable
          : PromotionAvailability.HotelUnavailable;

        const rewardsValue =
          hasAllPrices && lodging.price
            ? Object.values(
                (
                  lodging.price.totalDiscountAwareTaxesAndFeesIncluded ??
                  lodging.price.totalDiscountAware
                ).priceWithNoDiscounts.rewards
              )[0]
            : undefined;
        const rewardsCurrency: RewardsCurrency | undefined = rewardsValue
          ? ValidRewardsCurrency.find(
              (currency) => currency === rewardsValue.currency
            )
          : undefined;

        return (
          <PremiumCollectionPromotionAvailabilityCard
            key={lodging.lodging.id}
            lodgingCollection={lodging.lodgingCollection}
            images={lodging.lodging.media}
            lodgingName={lodging.lodging.name}
            lodgingRating={hotelStarRatingToNumber(lodging.lodging.starRating)}
            lodgingCity={lodging.lodging.city}
            strikethroughPricePerNight={formatPrice(
              lodging.price?.nightlyDiscountAware?.priceWithAllDiscounts
            )}
            pricePerNight={formatPrice(
              lodging.price?.nightlyDiscountAware?.priceWithNoDiscounts
            )}
            strikethroughPriceTotal={formatPrice(
              (
                lodging.price?.totalDiscountAwareTaxesAndFeesIncluded ??
                lodging.price?.totalDiscountAware
              )?.priceWithAllDiscounts
            )}
            priceTotal={formatPrice(
              (
                lodging.price?.totalDiscountAwareTaxesAndFeesIncluded ??
                lodging.price?.totalDiscountAware
              )?.priceWithNoDiscounts
            )}
            nights={dayjs(params.stayDates.until).diff(
              dayjs(params.stayDates.from),
              "days"
            )}
            totalPriceInMiles={formatRewards(rewardsValue)}
            promotionAvailability={offerAvailability}
            promotionStartDate={
              new Date(lodging.nthNightPromotion.promotionStartDate)
            }
            promotionEndDate={
              new Date(lodging.nthNightPromotion.promotionEndDate)
            }
            rewards={
              rewardsCurrency &&
              rewardsAccount && {
                rewardsAccount,
                rewardCurrency: rewardsCurrency,
                rewardsUiVariant: "hotels",
              }
            }
            onClick={() => {
              /* TODO: CLUX-641 - Tracking */
              setSelectedLodgingIndex(index);
              if (lodging.available) {
                const shopParams = transformToStringifiedQuery({
                  lodgingId: lodging.lodging.id,
                  lodgingSelection: "",
                  fromDate: new Date(params.stayDates.from),
                  untilDate: new Date(params.stayDates.until),
                  adultsCount: params.guests.adults,
                  childrenCount: params.guests.children.length,
                  // TODO: CLUX-627 - Populate the selectedAccountIndex properly
                  selectedAccountIndex: 0,
                  selectedLodgingIndex: index,
                  shopToken: cacheHotelTokenEnabled
                    ? lodging.price?.opaqueShopRequest
                    : localCache.set(
                        uuidv4(),
                        lodging.price?.opaqueShopRequest || ""
                      ).key,
                });
                if (matchesMobile)
                  history.push(`${PATH_SHOP}${shopParams}`, {
                    fromPage: history.location.pathname,
                  });
                else window.open(`${PATH_SHOP}${shopParams}`, "_blank");
              }
            }}
          />
        );
      })}
    </Box>
  );
};

export const PremiumCollectionOffers = ({
  lodgingPromotionResponse,
  fetchPremiumOffersShop,
  rewardsAccount,
  setSelectedLodgingIndex,
  ...rest
}: PremierCollectionAvailabilityConnectorProps & RouterProps) => {
  validateRestProps<PremierCollectionAvailabilityConnectorProps>(rest);

  const history = useHistory();
  const expState = useExperiments();
  const searchParams = useOffersSearchParams();

  // TODO: CLUX-627 - We want to split the experiment into two: one for the feature, and one for the entry points. We'll keep this experiment for the feature and create a new one for the entry points.
  // Check if user is in the LUXURY_COMPLIMENTARY_NIGHT_PROMOTION experiment
  const complimentaryNightPromotionVariant = getExperimentVariant(
    expState.experiments,
    LUXURY_COMPLIMENTARY_NIGHT_PROMOTION
  );

  // Redirect to PremiumSearch if not in AVAILABLE variant
  useEffect(() => {
    if (complimentaryNightPromotionVariant !== AVAILABLE) {
      history.replace(PATH_HOME);
    }
  }, [complimentaryNightPromotionVariant, history]);

  // Don't render if not in experiment
  if (complimentaryNightPromotionVariant !== AVAILABLE) {
    return null;
  }

  useEffect(() => {
    fetchPremiumOffersShop(history, searchParams);
  }, [fetchPremiumOffersShop, history, searchParams]);

  const cacheHotelTokenVariant = getExperimentVariant(
    expState.experiments,
    CACHE_HOTEL_TOKEN
  );

  const cacheHotelTokenEnabled = React.useMemo(
    () => cacheHotelTokenVariant === AVAILABLE,
    [cacheHotelTokenVariant]
  );

  const renderContent = () => {
    switch (lodgingPromotionResponse.status) {
      case REQUEST_STATUS.NOT_CALLED:
        // TODO: CLUX-627 - Not called state
        return null;
      case REQUEST_STATUS.IN_PROGRESS:
        // TODO: CLUX-627 - Loading state with skeleton
        return (
          <Box
            style={{
              width: "100%",
              padding: "32px 118px",
            }}
          >
            <Typography>TODO: CLUX-627 - Loading...</Typography>
          </Box>
        );
      case REQUEST_STATUS.SUCCESS:
        return (
          <PremiumCollectionOffersSuccess
            params={lodgingPromotionResponse.params}
            data={lodgingPromotionResponse.data}
            rewardsAccount={rewardsAccount}
            setSelectedLodgingIndex={setSelectedLodgingIndex}
            cacheHotelTokenEnabled={cacheHotelTokenEnabled}
          />
        );
      case REQUEST_STATUS.FAILED:
        // TODO: CLUX-627 - Error state
        return (
          <Box>
            <Typography>
              Error: {String(lodgingPromotionResponse.error)}
            </Typography>
          </Box>
        );
      default:
        return ensureExhaustive(lodgingPromotionResponse);
    }
  };

  return (
    <Box
      style={{
        // TODO: CLUX-627 - Move to SCSS module
        marginTop: 80,
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      {/* TODO: CLUX-627 - Header + style in SCSS module */}
      <Box
        style={{
          width: "100%",
          height: 336,
          backgroundColor: "#F7F3EB",
          padding: "32px 118px",
        }}
      >
        <Typography variant="h1">TODO: CLUX-627 - Header</Typography>
      </Box>

      <PremiumCollectionLodgingPromotionSearch
        onSearchChange={(
          startDate: Date,
          endDate: Date,
          travelersCounts: GuestsSelection
        ) => {
          fetchPremiumOffersShop(history, {
            stayDates: {
              from: dayjs(startDate).format("YYYY-MM-DD"),
              until: dayjs(endDate).format("YYYY-MM-DD"),
            },
            guests: {
              adults: travelersCounts.adults,
              children: travelersCounts.children,
            },
            rooms: {
              numberOfRooms: travelersCounts.rooms,
            },
            platform: PlatformEnum.Desktop,
            lodgingCollection: LodgingCollectionEnum.Premier,
          });
        }}
        startDate={dayjs(searchParams.stayDates.from).toDate()}
        endDate={dayjs(searchParams.stayDates.until).toDate()}
        months={[]}
        // TODO: CLUX-627 - Derive from results
        priceTags={[]}
        travelersCounts={{
          adults: searchParams.guests.adults,
          children: searchParams.guests.children,
          rooms: searchParams.rooms?.numberOfRooms ?? 1,
        }}
        // TODO: CLUX-627 - Set the same max as the Premium Search
        maximumTravelersCount={16}
        showRoomCounts={false}
        // TODO: CLUX-627 - Derive from results + add a map for know labels/icons
        filterCategories={[
          {
            label: "All",
          },
        ]}
        filterCategoryValue={0}
        onFilterCategoryChange={(value) => {
          // TODO: CLUX-627 - Implement this
          // eslint-disable-next-line no-console
          console.log(value);
        }}
        // TODO: CLUX-627 - Move to TextConstants
        sortOptions={[
          {
            label: "Recommended",
            value: "recommended",
          },
          {
            label: "Price (Low to High)",
            value: "price-asc",
          },
          {
            label: "Price (High to Low)",
            value: "price-desc",
          },
        ]}
        // TODO: CLUX-627 - Provide dynamic value
        sortValue="recommended"
        onSortChange={(value) => {
          // TODO: CLUX-627 - Implement this
          // eslint-disable-next-line no-console
          console.log(value);
        }}
      />
      {renderContent()}
    </Box>
  );
};
